import React from 'react';
import { Mail, Linkedin, Zap } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export function Footer() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleNavigation = (path: string) => {
    navigate(path);
    // Add a small delay to ensure navigation completes before scrolling
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }, 100);
  };

  const handleAuthClick = (e: React.MouseEvent, path: string) => {
    e.preventDefault();
    if (!user) {
      window.location.href = '/?modal=signin';
    } else {
      handleNavigation(path);
    }
  };

  return (
    <footer className="bg-bw-sidebar text-white mt-auto">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 lg:gap-12">
          {/* Company Info */}
          <div className="col-span-1">
            <div className="flex items-center mb-4">
              <Zap className="h-8 w-8 text-bw-accent" />
              <span className="ml-2 text-2xl font-bold text-white">BidWin</span>
            </div>
            <p className="text-white/60 text-sm mb-4">
              by{' '}
              <a 
                href="https://servetdigital.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-white/60 hover:text-bw-accent transition-colors"
              >
                SerVet Digital
              </a>
            </p>
            <p className="text-white/60 text-sm mb-6">
              Empowering businesses to win more government contracts through AI-powered insights and tools.
            </p>
            <div className="flex space-x-4">
              <a 
                href="https://www.linkedin.com/company/96656004" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-white/60 hover:text-bw-accent transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-4 text-white">Quick Links</h4>
            <ul className="space-y-3">
              <li>
                <button 
                  onClick={(e) => handleAuthClick(e, '/dashboard')}
                  className="text-white/60 hover:text-bw-accent transition-colors"
                >
                  Dashboard
                </button>
              </li>
              <li>
                <button 
                  onClick={(e) => handleAuthClick(e, '/dashboard')}
                  className="text-white/60 hover:text-bw-accent transition-colors"
                >
                  My Proposals
                </button>
              </li>
              <li>
                <button 
                  onClick={() => handleNavigation('/support')}
                  className="text-white/60 hover:text-bw-accent transition-colors"
                >
                  Support
                </button>
              </li>
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="text-lg font-semibold mb-4 text-white">Resources</h4>
            <ul className="space-y-3">
              <li>
                <button
                  onClick={() => handleNavigation('/docs')}
                  className="text-white/60 hover:text-bw-accent transition-colors"
                >
                  Documentation
                </button>
              </li>
              <li>
                <button
                  onClick={() => handleNavigation('/tutorials')}
                  className="text-white/60 hover:text-bw-accent transition-colors"
                >
                  Tutorials
                </button>
              </li>
              <li>
                <button
                  onClick={() => handleNavigation('/blog')}
                  className="text-white/60 hover:text-bw-accent transition-colors"
                >
                  The Winning Bid
                </button>
              </li>
              <li>
                <button
                  onClick={() => handleNavigation('/faq')}
                  className="text-white/60 hover:text-bw-accent transition-colors"
                >
                  FAQ
                </button>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-lg font-semibold mb-4 text-white">Contact Us</h4>
            <ul className="space-y-3">
              <li className="flex items-center text-white/60">
                <Mail className="h-5 w-5 mr-2 text-bw-accent" />
                <a 
                  href="mailto:info@servetdigital.com" 
                  className="hover:text-bw-accent transition-colors"
                >
                  info@servetdigital.com
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            {/* Copyright */}
            <p className="text-white/60 text-sm">
              &copy; {new Date().getFullYear()}{' '}
              <a 
                href="https://servetdigital.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:text-bw-accent transition-colors"
              >
                SerVet Digital
              </a>
              . All rights reserved.
            </p>

            {/* Legal Links */}
            <div className="flex items-center space-x-6">
              <button 
                onClick={() => handleNavigation('/terms')}
                className="text-white/60 hover:text-bw-accent text-sm transition-colors"
              >
                Terms of Service
              </button>
              <button 
                onClick={() => handleNavigation('/privacy')}
                className="text-white/60 hover:text-bw-accent text-sm transition-colors"
              >
                Privacy Policy
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}